import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";
import config from "../../config";
import { getAuthToken } from "../../utils/authStorage";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const currentDate = new Date();
const CURRENT_MONTH = String(currentDate.getMonth() + 1);
const CURRENT_YEAR = String(currentDate.getFullYear());

const formatAmount = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  return `LKR ${Number(value).toLocaleString("en-LK", { minimumFractionDigits: 2 })}`;
};

const displayValue = (value) => (value === null || value === undefined || value === "" ? "—" : value);

const requestConfig = () => {
  const token = getAuthToken();
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

const monthRange = (month, year) => {
  const monthNumber = Number(month);
  const yearNumber = Number(year);
  const monthText = String(monthNumber).padStart(2, "0");
  const lastDay = new Date(yearNumber, monthNumber, 0).getDate();
  return {
    from_date: `${yearNumber}-${monthText}-01`,
    to_date: `${yearNumber}-${monthText}-${String(lastDay).padStart(2, "0")}`,
  };
};

const primaryActionSx = {
  minHeight: 42,
  px: 2.25,
  borderRadius: 1.5,
  textTransform: "none",
  fontWeight: 800,
  color: "#ffffff",
  backgroundColor: "#246b8f",
  boxShadow: "0 3px 10px rgba(36, 107, 143, 0.22)",
  "&:hover": {
    backgroundColor: "#1b526e",
    boxShadow: "0 5px 14px rgba(36, 107, 143, 0.3)",
  },
  "&:active": { transform: "translateY(1px)" },
  "&.Mui-disabled": {
    color: "rgba(255,255,255,0.7)",
    backgroundColor: "rgba(36, 107, 143, 0.45)",
  },
};

const pdfActionSx = {
  ...primaryActionSx,
  backgroundColor: "#2f6f53",
  boxShadow: "0 3px 10px rgba(47, 111, 83, 0.22)",
  "&:hover": {
    backgroundColor: "#24583f",
    boxShadow: "0 5px 14px rgba(47, 111, 83, 0.3)",
  },
  "&.Mui-disabled": {
    color: "rgba(255,255,255,0.7)",
    backgroundColor: "rgba(47, 111, 83, 0.45)",
  },
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "background.paper",
    borderRadius: 1.25,
    "&:hover fieldset": { borderColor: "#246b8f" },
    "&.Mui-focused fieldset": { borderColor: "#246b8f", borderWidth: 2 },
  },
};

const tableContainerSx = {
  borderRadius: 1.5,
  borderColor: "divider",
  boxShadow: "0 3px 14px rgba(15, 23, 42, 0.06)",
};

const tableSx = {
  "& .MuiTableHead-root": { backgroundColor: "primary.main" },
  "& .MuiTableCell-head": {
    color: "primary.contrastText",
    fontWeight: 800,
    fontSize: "0.76rem",
    letterSpacing: "0.02em",
    whiteSpace: "nowrap",
    py: 1.25,
  },
  "& .MuiTableCell-body": { py: 1.15, borderColor: "divider" },
  "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)": {
    backgroundColor: "action.hover",
  },
  "& .MuiTableBody-root .MuiTableRow-root:hover": {
    backgroundColor: "rgba(36, 107, 143, 0.08)",
  },
};

const amountCellSx = { fontWeight: 800, color: "text.primary", whiteSpace: "nowrap" };

const ReportSection = ({ title, description, children }) => (
  <Box component="section" sx={{ py: { xs: 2, sm: 2.75 } }}>
    <Card
      variant="outlined"
      sx={{
        borderColor: "divider",
        borderRadius: 2,
        backgroundColor: "background.paper",
        boxShadow: "0 4px 18px rgba(15, 23, 42, 0.06)",
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.75 }, "&:last-child": { pb: { xs: 2, sm: 2.75 } } }}>
        <Box sx={{ borderLeft: "4px solid #246b8f", pl: 1.5, mb: 2.25 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.35, fontSize: { xs: "1.15rem", sm: "1.35rem" } }}>{title}</Typography>
          <Typography variant="body2" color="text.secondary">{description}</Typography>
        </Box>
        {children}
      </CardContent>
    </Card>
  </Box>
);

const FilterGrid = ({ children }) => (
  <Box
    display="grid"
    gap={{ xs: 1.5, sm: 2 }}
    gridTemplateColumns={{ xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" }}
    alignItems="center"
    mb={2.25}
    sx={{
      p: { xs: 1.5, sm: 2 },
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1.5,
      backgroundColor: "action.hover",
    }}
  >
    {children}
  </Box>
);

const MonthYearFilters = ({ month, year, onMonthChange, onYearChange }) => (
  <>
    <FormControl fullWidth size="small" sx={fieldSx}>
      <InputLabel>Month</InputLabel>
      <Select value={month} label="Month" onChange={(event) => onMonthChange(event.target.value)}>
        {MONTHS.map((label, index) => <MenuItem key={label} value={String(index + 1)}>{label}</MenuItem>)}
      </Select>
    </FormControl>
    <TextField
      fullWidth
      size="small"
      type="number"
      label="Year"
      value={year}
      onChange={(event) => onYearChange(event.target.value)}
      inputProps={{ min: 1000, max: 9999 }}
      sx={fieldSx}
    />
  </>
);

const ReportState = ({ loading, error, hasData, children }) => {
  if (loading) return <Box display="flex" alignItems="center" justifyContent="center" gap={1.25} py={3} color="text.secondary"><CircularProgress size={24} /><Typography variant="body2">Loading report...</Typography></Box>;
  if (error) return <Alert severity="error" variant="outlined" sx={{ mb: 2, borderRadius: 1.5 }}>{error}</Alert>;
  if (!hasData) return <Alert severity="info" variant="outlined" sx={{ borderRadius: 1.5 }}>Run the report to view results.</Alert>;
  return children;
};

const Metric = ({ label, value }) => (
  <Box sx={{ p: { xs: 1.5, sm: 1.75 }, minHeight: 82, borderRadius: 1.5, border: "1px solid", borderColor: "divider", borderLeft: "4px solid #246b8f", backgroundColor: "action.hover", boxShadow: "0 2px 8px rgba(15, 23, 42, 0.05)" }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase" }}>{label}</Typography>
    <Typography variant="h6" fontWeight={800} sx={{ mt: 0.5, wordBreak: "break-word" }}>{value}</Typography>
  </Box>
);

const TebaPaymentReports = () => {
  const [disciplines, setDisciplines] = useState([]);
  const [disciplineError, setDisciplineError] = useState("");

  const [monthlyMonth, setMonthlyMonth] = useState(CURRENT_MONTH);
  const [monthlyYear, setMonthlyYear] = useState(CURRENT_YEAR);
  const [monthlyState, setMonthlyState] = useState({ loading: false, error: "", data: null });

  const [disciplineMonth, setDisciplineMonth] = useState(CURRENT_MONTH);
  const [disciplineYear, setDisciplineYear] = useState(CURRENT_YEAR);
  const [disciplineState, setDisciplineState] = useState({ loading: false, error: "", data: null });

  const [rangeFrom, setRangeFrom] = useState(`${CURRENT_YEAR}-${String(CURRENT_MONTH).padStart(2, "0")}-01`);
  const [rangeTo, setRangeTo] = useState(currentDate.toISOString().slice(0, 10));
  const [rangeState, setRangeState] = useState({ loading: false, error: "", data: null });

  const [detailFrom, setDetailFrom] = useState(`${CURRENT_YEAR}-${String(CURRENT_MONTH).padStart(2, "0")}-01`);
  const [detailTo, setDetailTo] = useState(currentDate.toISOString().slice(0, 10));
  const [detailDiscipline, setDetailDiscipline] = useState("");
  const [detailStudentName, setDetailStudentName] = useState("");
  const [detailState, setDetailState] = useState({ loading: false, error: "", data: null });

  const [outstandingMonth, setOutstandingMonth] = useState(CURRENT_MONTH);
  const [outstandingYear, setOutstandingYear] = useState(CURRENT_YEAR);
  const [outstandingDiscipline, setOutstandingDiscipline] = useState("");
  const [outstandingState, setOutstandingState] = useState({ loading: false, error: "", data: null });
  const [pdfLoading, setPdfLoading] = useState("");
  const [pdfError, setPdfError] = useState("");

  useEffect(() => {
    const loadDisciplines = async () => {
      setDisciplineError("");
      try {
        const response = await axios.get(`${config.apiBaseUrl}/get_disciplines`, requestConfig());
        setDisciplines(response.data.disciplines || []);
      } catch (error) {
        setDisciplineError(error.response?.data?.error || "Could not load disciplines.");
      }
    };
    loadDisciplines();
  }, []);

  const runMonthly = async () => {
    setMonthlyState({ loading: true, error: "", data: null });
    try {
      const response = await axios.get(`${config.apiBaseUrl}/reports/teba/payments/monthly-summary`, {
        ...requestConfig(),
        params: { month: monthlyMonth, year: monthlyYear },
      });
      setMonthlyState({ loading: false, error: "", data: response.data });
    } catch (error) {
      setMonthlyState({ loading: false, error: error.response?.data?.error || "Could not load monthly collection.", data: null });
    }
  };

  const runDiscipline = async () => {
    const range = monthRange(disciplineMonth, disciplineYear);
    setDisciplineState({ loading: true, error: "", data: null });
    try {
      const response = await axios.get(`${config.apiBaseUrl}/reports/teba/payments/by-discipline`, {
        ...requestConfig(),
        params: range,
      });
      setDisciplineState({ loading: false, error: "", data: response.data });
    } catch (error) {
      setDisciplineState({ loading: false, error: error.response?.data?.error || "Could not load discipline collection.", data: null });
    }
  };

  const runDateRange = async () => {
    if (!rangeFrom || !rangeTo || rangeFrom > rangeTo) {
      setRangeState({ loading: false, error: "From date must be on or before to date.", data: null });
      return;
    }
    setRangeState({ loading: true, error: "", data: null });
    try {
      const response = await axios.get(`${config.apiBaseUrl}/reports/teba/payments/date-range`, {
        ...requestConfig(),
        params: { from_date: rangeFrom, to_date: rangeTo },
      });
      setRangeState({ loading: false, error: "", data: response.data });
    } catch (error) {
      setRangeState({ loading: false, error: error.response?.data?.error || "Could not load date-range collection.", data: null });
    }
  };

  const runDetailed = async () => {
    if (!detailFrom || !detailTo || detailFrom > detailTo) {
      setDetailState({ loading: false, error: "From date must be on or before to date.", data: null });
      return;
    }
    setDetailState({ loading: true, error: "", data: null });
    const params = { from_date: detailFrom, to_date: detailTo };
    if (detailDiscipline) params.discipline_id = detailDiscipline;
    if (detailStudentName.trim()) params.student_name = detailStudentName.trim();
    try {
      const response = await axios.get(`${config.apiBaseUrl}/reports/teba/payments/detailed`, {
        ...requestConfig(),
        params,
      });
      setDetailState({ loading: false, error: "", data: response.data });
    } catch (error) {
      setDetailState({ loading: false, error: error.response?.data?.error || "Could not load detailed payments.", data: null });
    }
  };

  const runOutstanding = async () => {
    if (!outstandingDiscipline) {
      setOutstandingState({ loading: false, error: "Select a discipline or Unassigned.", data: null });
      return;
    }
    setOutstandingState({ loading: true, error: "", data: null });
    try {
      const response = await axios.get(`${config.apiBaseUrl}/reports/teba/payments/outstanding`, {
        ...requestConfig(),
        params: {
          month: outstandingMonth,
          year: outstandingYear,
          discipline_id: outstandingDiscipline,
        },
      });
      setOutstandingState({ loading: false, error: "", data: response.data });
    } catch (error) {
      setOutstandingState({ loading: false, error: error.response?.data?.error || "Could not load outstanding fees.", data: null });
    }
  };

  const downloadPdf = async (key, endpoint, params, filename) => {
    setPdfLoading(key);
    setPdfError("");
    try {
      const response = await axios.get(`${config.apiBaseUrl}${endpoint}`, {
        ...requestConfig(),
        params,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setPdfError(error.response?.data?.error || "Could not generate the PDF report.");
    } finally {
      setPdfLoading("");
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5, letterSpacing: "0.02em" }}>TEBA Payment Reports</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Family-level TEBA payment and outstanding-fee reports.
      </Typography>
      {pdfError && <Alert severity="error" variant="outlined" sx={{ mb: 2, borderRadius: 1.5 }}>{pdfError}</Alert>}

      <ReportSection title="Monthly Collection Summary" description="Total TEBA fees received in the selected calendar month.">
        <FilterGrid>
          <MonthYearFilters month={monthlyMonth} year={monthlyYear} onMonthChange={setMonthlyMonth} onYearChange={setMonthlyYear} />
          <Button variant="contained" startIcon={<SearchIcon />} onClick={runMonthly} disabled={monthlyState.loading} sx={primaryActionSx}>View Summary</Button>
          <Button variant="contained" startIcon={<DownloadIcon />} onClick={() => downloadPdf("monthly", "/reports/teba/payments/monthly-summary/pdf", { month: monthlyMonth, year: monthlyYear }, `teba_monthly_collection_${monthlyYear}_${String(monthlyMonth).padStart(2, "0")}.pdf`)} disabled={Boolean(pdfLoading)} sx={pdfActionSx}>{pdfLoading === "monthly" ? "Generating..." : "Download PDF"}</Button>
        </FilterGrid>
        <ReportState loading={monthlyState.loading} error={monthlyState.error} hasData={Boolean(monthlyState.data)}>
          <Box display="grid" gap={1.5} gridTemplateColumns={{ xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)" }}>
            <Metric label="Month" value={monthlyState.data ? `${MONTHS[Number(monthlyState.data.month) - 1]} ${monthlyState.data.year}` : "—"} />
            <Metric label="Total Collection" value={formatAmount(monthlyState.data?.total_collection)} />
            <Metric label="Payments" value={displayValue(monthlyState.data?.payment_count)} />
          </Box>
        </ReportState>
      </ReportSection>

      <ReportSection title="Discipline-wise Collection" description="Collection grouped by the family account owner's primary discipline.">
        <FilterGrid>
          <MonthYearFilters month={disciplineMonth} year={disciplineYear} onMonthChange={setDisciplineMonth} onYearChange={setDisciplineYear} />
          <Button variant="contained" startIcon={<SearchIcon />} onClick={runDiscipline} disabled={disciplineState.loading} sx={primaryActionSx}>View Collection</Button>
          <Button variant="contained" startIcon={<DownloadIcon />} onClick={() => downloadPdf("discipline", "/reports/teba/payments/by-discipline/pdf", { month: disciplineMonth, year: disciplineYear }, `teba_discipline_collection_${disciplineYear}_${String(disciplineMonth).padStart(2, "0")}.pdf`)} disabled={Boolean(pdfLoading)} sx={pdfActionSx}>{pdfLoading === "discipline" ? "Generating..." : "Download PDF"}</Button>
        </FilterGrid>
        <ReportState loading={disciplineState.loading} error={disciplineState.error} hasData={Boolean(disciplineState.data)}>
          {disciplineState.data?.rows?.length ? (
            <TableContainer component={Paper} variant="outlined" sx={tableContainerSx}><Table size="small" sx={tableSx}><TableHead><TableRow><TableCell>Discipline</TableCell><TableCell align="right">Families Paid</TableCell><TableCell align="right">Fees Received</TableCell></TableRow></TableHead><TableBody>
              {disciplineState.data.rows.map((row) => <TableRow key={row.discipline_id ?? "unassigned"}><TableCell>{displayValue(row.discipline)}</TableCell><TableCell align="right">{displayValue(row.total_students_paid)}</TableCell><TableCell align="right" sx={amountCellSx}>{formatAmount(row.total_fees_received)}</TableCell></TableRow>)}
              <TableRow><TableCell sx={{ fontWeight: 800 }}>Overall Total</TableCell><TableCell /><TableCell align="right" sx={amountCellSx}>{formatAmount(disciplineState.data.overall_total_collection)}</TableCell></TableRow>
            </TableBody></Table></TableContainer>
          ) : <Alert severity="info" variant="outlined" sx={{ borderRadius: 1.5 }}>No discipline collection rows were returned.</Alert>}
        </ReportState>
      </ReportSection>

      <ReportSection title="Date Range Collection" description="Total TEBA fees received within the selected inclusive date range.">
        <FilterGrid>
          <TextField fullWidth size="small" type="date" label="From date" value={rangeFrom} onChange={(event) => setRangeFrom(event.target.value)} InputLabelProps={{ shrink: true }} sx={fieldSx} />
          <TextField fullWidth size="small" type="date" label="To date" value={rangeTo} onChange={(event) => setRangeTo(event.target.value)} InputLabelProps={{ shrink: true }} sx={fieldSx} />
          <Button variant="contained" startIcon={<SearchIcon />} onClick={runDateRange} disabled={rangeState.loading} sx={primaryActionSx}>View Collection</Button>
          <Button variant="contained" startIcon={<DownloadIcon />} onClick={() => downloadPdf("range", "/reports/teba/payments/date-range/pdf", { from_date: rangeFrom, to_date: rangeTo }, `teba_collection_${rangeFrom}_${rangeTo}.pdf`)} disabled={Boolean(pdfLoading)} sx={pdfActionSx}>{pdfLoading === "range" ? "Generating..." : "Download PDF"}</Button>
        </FilterGrid>
        <ReportState loading={rangeState.loading} error={rangeState.error} hasData={Boolean(rangeState.data)}>
          <Box display="grid" gap={1.5} gridTemplateColumns={{ xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)" }}>
            <Metric label="Selected Period" value={`${rangeState.data?.from_date} to ${rangeState.data?.to_date}`} />
            <Metric label="Total Collection" value={formatAmount(rangeState.data?.total_collection)} />
            <Metric label="Payments" value={displayValue(rangeState.data?.payment_count)} />
          </Box>
        </ReportState>
      </ReportSection>

      <ReportSection title="Detailed Payment Report" description="Payment rows using the existing TEBA family-level fields.">
        <FilterGrid>
          <TextField fullWidth size="small" type="date" label="From date" value={detailFrom} onChange={(event) => setDetailFrom(event.target.value)} InputLabelProps={{ shrink: true }} sx={fieldSx} />
          <TextField fullWidth size="small" type="date" label="To date" value={detailTo} onChange={(event) => setDetailTo(event.target.value)} InputLabelProps={{ shrink: true }} sx={fieldSx} />
          <FormControl fullWidth size="small" sx={fieldSx}><InputLabel>Discipline</InputLabel><Select value={detailDiscipline} label="Discipline" onChange={(event) => setDetailDiscipline(event.target.value)}><MenuItem value="">All disciplines</MenuItem>{disciplines.map((discipline) => <MenuItem key={discipline.discipline_id} value={discipline.discipline_id}>{discipline.discipline_name}</MenuItem>)}</Select></FormControl>
          <TextField fullWidth size="small" label="Family account owner" value={detailStudentName} onChange={(event) => setDetailStudentName(event.target.value)} sx={fieldSx} />
          <Button variant="contained" startIcon={<SearchIcon />} onClick={runDetailed} disabled={detailState.loading} sx={primaryActionSx}>View Details</Button>
          <Button variant="contained" startIcon={<DownloadIcon />} onClick={() => downloadPdf("detailed", "/reports/teba/payments/detailed/pdf", { from_date: detailFrom, to_date: detailTo, ...(detailDiscipline ? { discipline_id: detailDiscipline } : {}), ...(detailStudentName.trim() ? { student_name: detailStudentName.trim() } : {}) }, `teba_detailed_payments_${detailFrom}_${detailTo}.pdf`)} disabled={Boolean(pdfLoading)} sx={pdfActionSx}>{pdfLoading === "detailed" ? "Generating..." : "Download PDF"}</Button>
        </FilterGrid>
        {disciplineError && <Alert severity="warning" variant="outlined" sx={{ mb: 2, borderRadius: 1.5 }}>{disciplineError}</Alert>}
        <ReportState loading={detailState.loading} error={detailState.error} hasData={Boolean(detailState.data)}>
          {detailState.data?.rows?.length ? (
            <TableContainer component={Paper} variant="outlined" sx={{ ...tableContainerSx, overflowX: "auto" }}><Table size="small" sx={{ ...tableSx, minWidth: 1200 }}><TableHead><TableRow><TableCell>Payment ID</TableCell><TableCell>Payment Date</TableCell><TableCell>Family Account Owner</TableCell><TableCell>Owner Member ID</TableCell><TableCell>Owner Membership ID</TableCell><TableCell>Family ID</TableCell><TableCell>Primary Discipline</TableCell><TableCell>Branch</TableCell><TableCell>Subscription</TableCell><TableCell>Type</TableCell><TableCell align="right">Amount</TableCell><TableCell>Expiry Date</TableCell></TableRow></TableHead><TableBody>
              {detailState.data.rows.map((row) => <TableRow key={row.payment_id}><TableCell>{displayValue(row.payment_id)}</TableCell><TableCell>{displayValue(row.payment_date)}</TableCell><TableCell sx={{ fontWeight: 700 }}>{displayValue(row.family_account_owner)}</TableCell><TableCell>{displayValue(row.owner_member_id)}</TableCell><TableCell>{displayValue(row.owner_membership_id)}</TableCell><TableCell>{displayValue(row.family_id)}</TableCell><TableCell>{displayValue(row.owner_primary_discipline)}</TableCell><TableCell>{displayValue(row.branch_name)}</TableCell><TableCell>{displayValue(row.subscription_name)}</TableCell><TableCell>{displayValue(row.subscription_type)}</TableCell><TableCell align="right" sx={amountCellSx}>{formatAmount(row.amount)}</TableCell><TableCell>{displayValue(row.expiry_date)}</TableCell></TableRow>)}
            </TableBody></Table></TableContainer>
          ) : <Alert severity="info" variant="outlined" sx={{ borderRadius: 1.5 }}>No payments matched the selected filters.</Alert>}
        </ReportState>
      </ReportSection>

      <ReportSection title="Outstanding Fees" description="Family-level amounts due for the selected month, year, and primary discipline.">
        <FilterGrid>
          <MonthYearFilters month={outstandingMonth} year={outstandingYear} onMonthChange={setOutstandingMonth} onYearChange={setOutstandingYear} />
          <FormControl fullWidth size="small" sx={fieldSx} error={Boolean(outstandingState.error && !outstandingDiscipline)}><InputLabel>Discipline</InputLabel><Select value={outstandingDiscipline} label="Discipline" onChange={(event) => setOutstandingDiscipline(event.target.value)}><MenuItem value="">Select discipline</MenuItem><MenuItem value="unassigned">Unassigned</MenuItem>{disciplines.map((discipline) => <MenuItem key={discipline.discipline_id} value={discipline.discipline_id}>{discipline.discipline_name}</MenuItem>)}</Select></FormControl>
          <Button variant="contained" startIcon={<SearchIcon />} onClick={runOutstanding} disabled={outstandingState.loading} sx={primaryActionSx}>View Outstanding</Button>
          <Button variant="contained" startIcon={<DownloadIcon />} onClick={() => downloadPdf("outstanding", "/reports/teba/payments/outstanding/pdf", { month: outstandingMonth, year: outstandingYear, discipline_id: outstandingDiscipline }, `teba_outstanding_${outstandingYear}_${String(outstandingMonth).padStart(2, "0")}.pdf`)} disabled={Boolean(pdfLoading) || !outstandingDiscipline} sx={pdfActionSx}>{pdfLoading === "outstanding" ? "Generating..." : "Download PDF"}</Button>
        </FilterGrid>
        <ReportState loading={outstandingState.loading} error={outstandingState.error} hasData={Boolean(outstandingState.data)}>
          {outstandingState.data?.rows?.length ? (
            <TableContainer component={Paper} variant="outlined" sx={tableContainerSx}><Table size="small" sx={tableSx}><TableHead><TableRow><TableCell>Family Account Owner</TableCell><TableCell>Owner Member ID</TableCell><TableCell>Owner Membership ID</TableCell><TableCell>Family ID</TableCell><TableCell>Discipline</TableCell><TableCell>Subscription</TableCell><TableCell>Type</TableCell><TableCell align="right">Amount Due</TableCell></TableRow></TableHead><TableBody>
              {outstandingState.data.rows.map((row) => <TableRow key={row.family_id}><TableCell sx={{ fontWeight: 700 }}>{displayValue(row.family_account_owner)}</TableCell><TableCell>{displayValue(row.owner_member_id)}</TableCell><TableCell>{displayValue(row.owner_membership_id)}</TableCell><TableCell>{displayValue(row.family_id)}</TableCell><TableCell>{displayValue(row.discipline)}</TableCell><TableCell>{displayValue(row.subscription)}</TableCell><TableCell>{displayValue(row.subscription_type)}</TableCell><TableCell align="right" sx={amountCellSx}>{formatAmount(row.amount_due)}</TableCell></TableRow>)}
              <TableRow><TableCell colSpan={7} sx={{ fontWeight: 800 }}>Total Outstanding</TableCell><TableCell align="right" sx={amountCellSx}>{formatAmount(outstandingState.data.total_outstanding)}</TableCell></TableRow>
            </TableBody></Table></TableContainer>
          ) : <Alert severity="info" variant="outlined" sx={{ borderRadius: 1.5 }}>No outstanding fees were returned for the selected filters.</Alert>}
        </ReportState>
      </ReportSection>
    </Box>
  );
};

export default TebaPaymentReports;
